import { COOKIE_IDS } from 'App/constants';
import { logError } from 'App/services/faroService';
import { REDEMPTION_STATUS_ENUMS } from 'Pages/rewards/constants';
import {
  REDDwollaRedemptionOption,
  REDPayPalRedemptionOption,
  REDVirtualIncentivesRedemptionOption
} from 'Shared/models/swagger/red';
import { getCookie} from 'Shared/services/cookieService';
import { getPaymentOptions, getRedUrl, postPaypalAuthCode, postRedemptionAPI } from 'Shared/services/red/api/redAPI';
import * as route from 'Shared/services/red/routes';
import { getUserCacheData, setUserCacheData,UserSessionCacheKeys } from 'Shared/services/sessionCacheService';
import { SwrKeys } from 'Shared/services/swr/swrKeys';
import { getEnvVar } from 'Shared/utils/envUtils';
import useSWR, { SWRResponse } from 'swr';

export type AuthCodeResponseModel = {
  paymentId: number;
  email    : string;
}

export type UserRedemptionOptions = REDDwollaRedemptionOption | REDPayPalRedemptionOption | REDVirtualIncentivesRedemptionOption[];

let paymentIdPromise:Promise<AuthCodeResponseModel | null> | null         = null;
let redemptionOptionsPromise:Promise<UserRedemptionOptions | null> | null = null;

const MAX_RETRY_ATTEMPTS     = 5;
const RETRY_DELAY_ONE_SECOND = 1000; // milliseconds

/**
 * Checks redemption status upon successful call to /redeem endpoint and retries it on error
 * @param statusURL
 * @param retryCount
 * @returns
 */
const handleSSEMessage = (
  statusURL: string,
  retryCount = 0
): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Create the SSE event
    const sseEventSource = new EventSource(statusURL);

    sseEventSource.onmessage = (event) => {
      const eventMessage = JSON.parse(event.data).status;

      switch (eventMessage) {
        case REDEMPTION_STATUS_ENUMS.succeeded:
          sseEventSource.close();
          resolve();
          break;
        case REDEMPTION_STATUS_ENUMS.aborted:
        case REDEMPTION_STATUS_ENUMS.failed:
        case REDEMPTION_STATUS_ENUMS.timedOut:
          sseEventSource.close();
          reject(null);
          break;
        default:
          break;
      }
    };

    sseEventSource.onerror = () => {
      // Retry logic
      if (retryCount < MAX_RETRY_ATTEMPTS) {
        setTimeout(() => {
          handleSSEMessage(statusURL, retryCount + 1)
            .then(resolve)
            .catch(reject);
        }, RETRY_DELAY_ONE_SECOND);
      } else {
        logError('handleSSEMessage', 'Error getting redemption status');
        reject(new Error('Error getting redemption status'));
      }
    };
  });
};

/**
 * POST redemption via paypal to RED MS
 * @param points
 */
const postUserRedemption = (
  points       : number,
  paymentId    : number,
  redemptionId : string
): Promise<void>  => {
  return new Promise((resolve, reject) => {
    postRedemptionAPI(points, paymentId, redemptionId).then((response) => {
      const sseId     = response.data.redemptionId;
      const statusURL = process.env.REACT_APP_RED_BASE_URL + getRedUrl(route.RED_REDEMPTION_STATUS) + `/${sseId}`;

      handleSSEMessage(statusURL)
        .then(() => resolve())
        .catch(reject);
    }).catch((error) => {
      logError('postRedemptionAPI', error);
      reject(error);
    });
  });
};

/**
 * Sends the Paypal auth code to the RED MS and returns the corresponding `userPaymentMethodId` for redemption
 * @param authCode - paypal authorization code
 */
const getPaymentIdFromAuthCode = (authCode: string): Promise<AuthCodeResponseModel | null> => {
  if (paymentIdPromise) {
    return paymentIdPromise;
  }

  paymentIdPromise = new Promise<AuthCodeResponseModel | null>((resolve, reject) => {
    postPaypalAuthCode(authCode)
      .then((response) => {
        const responseModel = {
          paymentId: response?.data?.id,
          email    : response?.data?.email
        };

        resolve(responseModel);
      })
      .catch((error) => {
        logError('postPaypalAuthCode', error);
        reject(error.response);
      })
      .finally(() => {
        paymentIdPromise = null;
      });
  });

  return paymentIdPromise;
};

/**
 * GET User Redemption Options
 */
const getUserRedemptionOptions = (): Promise<UserRedemptionOptions | null> | null => {
  if (redemptionOptionsPromise) {
    return redemptionOptionsPromise;
  }

  redemptionOptionsPromise = new Promise<UserRedemptionOptions>((resolve, reject) => {
    getPaymentOptions()
      .then((response) => {
        const redemptionOptions = response?.data?.redemptionOptions || [];
        setUserCacheData(UserSessionCacheKeys.REDEMPTION_OPTIONS, redemptionOptions);
        resolve(redemptionOptions);
      })
      .catch(error => {
        const fallbackRedemptionOptions: UserRedemptionOptions = [];
        logError('getPaymentOptions', error);
        reject(fallbackRedemptionOptions);
      })
      .finally(() => {
        redemptionOptionsPromise = null;
      });
  });

  return redemptionOptionsPromise;
};

/**
 * @returns {SWRResponse<any, any>}
 */
const useGetRedemptionOptions = (): SWRResponse<any, any> => {
  const initialData = getUserCacheData(UserSessionCacheKeys.REDEMPTION_OPTIONS) ;

  const swr = useSWR(SwrKeys.PaymentOptions, getUserRedemptionOptions, {
    fallbackData: initialData
  });

  let isLoading = swr.isLoading;

  if (typeof initialData !== 'undefined') {
    isLoading = false;
  }

  return {
    ...swr,
    isLoading
  };
};

/**
 * Gets the idempotency key cookie
 * @returns {string}
 */
export const getIdempotencyKeyCookie = (): string => {
  return getCookie(COOKIE_IDS.REWARDS_IDEMPOTENCY_KEY);
};

/**
 * Function to check if the EMAIL_CODE_VERIFICATION_ENABLED config is enabled
 * @returns {boolean}
 */
const isCodeVerificationConfigEnabled = (): boolean => {
  return getEnvVar('EMAIL_CODE_VERIFICATION_ENABLED');
};

export {
  getPaymentIdFromAuthCode,
  getUserRedemptionOptions,
  isCodeVerificationConfigEnabled,
  postUserRedemption,
  useGetRedemptionOptions
};
